import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Trip, TripPayload } from '../../core/models/trip.model';
import { Vehicle } from '../../core/models/vehicle.model';
import { Route } from '../../core/models/route.model';
import { Driver } from '../../core/models/driver.model';
import { TripService } from '../../core/services/trip.service';
import { VehicleService } from '../../core/services/vehicle.service';
import { RouteService } from '../../core/services/route.service';
import { DriverService } from '../../core/services/driver.service';
import { AuthService } from '../../core/services/auth.service';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { StatusID } from '../../core/enums/status-id.enum';
import { RecurringTripSchedule, RecurringTripSchedulePayload } from '../../core/models/recurring-trip.model';
import { RecurringTripService } from '../../core/services/recurring-trip.service';
import { finalize } from 'rxjs/operators';
import { ConfirmationService } from '../../core/services/confirmation.service';

interface CalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  trips: Trip[];
}

@Component({
  selector: 'app-trip-scheduling',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './trip-scheduling.component.html',
  styleUrls: ['./management-page.component.css']
})
export class TripSchedulingComponent implements OnInit {
  trips: Trip[] = [];
  vehicles: Vehicle[] = [];
  routes: Route[] = [];
  drivers: Driver[] = [];
  tableDrivers: Driver[] = [];
  recurringDrivers: Driver[] = [];
  recurringSchedules: RecurringTripSchedule[] = [];
  showForm = false;
  isEditing = false;
  selectedTrip: Trip | null = null;
  duplicateSource: Trip | null = null;
  selectedRecurringSchedule: RecurringTripSchedule | null = null;
  showRecurringForm = false;
  isSavingRecurring = false;
  formSubmissionAttempted = false;
  recurringSubmissionAttempted = false;
  errorMessage = '';
  successMessage = '';
  driverValidationMessage = '';
  organisationValidationMessage = '';
  calendarDate = new Date();
  
  tripForm: FormGroup;
  recurringForm: FormGroup;
  selectedWeekdays = new Set<number>();
  readonly weekdays = [
    { value: 0, label: 'Sun' }, { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' }, { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' }
  ];

  private readonly currentUser;
  private readonly currentRole: UserRole;
  readonly isAdminOrPlatform: boolean;

  constructor(
    private fb: FormBuilder,
    private tripService: TripService,
    private vehicleService: VehicleService,
    private routeService: RouteService,
    private driverService: DriverService,
    private authService: AuthService,
    private recurringTripService: RecurringTripService,
    private confirmations: ConfirmationService
  ) {
    this.currentUser = this.authService.getCurrentUser();
    this.currentRole = normalizeUserRole(
      this.currentUser?.userRoleName || this.currentUser?.userRole || this.currentUser?.userRoleID
    );
    this.isAdminOrPlatform = this.currentRole === UserRole.ADMIN || this.currentRole === UserRole.PLATFORM_EMPLOYEE;

    this.tripForm = this.fb.group({
      tripName: ['', Validators.required],
      routeID: [null, Validators.required],
      vehicleID: [null, Validators.required],
      driverID: [null, Validators.required],
      tripStartTime: ['', Validators.required],
      tripEndTime: ['', Validators.required],
      tripCapacity: [null, [Validators.required, Validators.min(1)]],
      tripSeatsAvailable: [null, [Validators.required, Validators.min(0)]]
    });

    this.recurringForm = this.fb.group({
      tripNameTemplate: ['', Validators.required],
      routeID: [null, Validators.required],
      vehicleID: [null, Validators.required],
      driverID: [null, Validators.required],
      tripCapacity: [null, [Validators.required, Validators.min(1)]],
      startTime: ['', Validators.required],
      durationMinutes: [60, [Validators.required, Validators.min(1), Validators.max(1439)]],
      frequency: ['Daily', Validators.required],
      interval: [1, [Validators.required, Validators.min(1)]],
      startDate: ['', Validators.required],
      endDate: [''],
      occurrenceCount: [null, Validators.min(1)],
      operatorTimeZone: ['Africa/Johannesburg', Validators.required],
      statusID: [StatusID.ACTIVE, Validators.required],
      generateDaysAhead: [30, [Validators.required, Validators.min(1), Validators.max(365)]]
    });
  }

  ngOnInit(): void {
    this.loadTrips();
    this.loadVehicles();
    this.loadRoutes();
    this.loadTableDrivers();
    this.loadRecurringSchedules();
    this.tripForm.get('vehicleID')?.valueChanges.subscribe(() => this.onRouteOrVehicleChanged());
    this.tripForm.get('routeID')?.valueChanges.subscribe(() => this.onRouteOrVehicleChanged());
    this.recurringForm.get('vehicleID')?.valueChanges.subscribe(() => this.onRecurringRouteOrVehicleChanged());
    this.recurringForm.get('routeID')?.valueChanges.subscribe(() => this.onRecurringRouteOrVehicleChanged());
  }

  loadTrips(): void {
    const organisationId = this.isAdminOrPlatform ? undefined : this.currentUser?.organisationID || undefined;
    this.tripService.getTrips(organisationId ? { organisationId } : undefined).subscribe({
      next: trips => this.trips = this.orderTrips(trips),
      error: () => this.errorMessage = 'Unable to load trips.'
    });
  }

  loadVehicles(): void {
    const organisationId = this.isAdminOrPlatform ? undefined : this.currentUser?.organisationID || undefined;
    this.vehicleService.getVehicles(organisationId).subscribe({
      next: vehicles => this.vehicles = vehicles,
      error: () => this.errorMessage = 'Unable to load vehicles.'
    });
  }

  loadRoutes(): void {
    const organisationId = this.isAdminOrPlatform ? undefined : this.currentUser?.organisationID || undefined;
    this.routeService.getRoutes({ organisationId, includeInactive: true }).subscribe({
      next: routes => this.routes = routes,
      error: () => this.errorMessage = 'Unable to load active routes.'
    });
  }

  loadTableDrivers(): void {
    const organisationId = this.isAdminOrPlatform ? undefined : this.currentUser?.organisationID || undefined;
    this.driverService.getDrivers({ organisationId, includeInactive: true }).subscribe({
      next: drivers => this.tableDrivers = drivers,
      error: () => this.tableDrivers = []
    });
  }

  loadRecurringSchedules(): void {
    const organisationId = this.isAdminOrPlatform ? undefined : this.currentUser?.organisationID || undefined;
    this.recurringTripService.getSchedules({ organisationId, includeInactive: true }).subscribe({
      next: schedules => this.recurringSchedules = schedules,
      error: () => this.errorMessage = 'Unable to load recurring trip schedules.'
    });
  }

  loadDrivers(organisationId?: number | null): void {
    if (!organisationId) {
      this.drivers = [];
      this.tripForm.get('driverID')?.setValue(null, { emitEvent: false });
      return;
    }

    this.driverService.getDrivers({ organisationId, includeInactive: false }).subscribe({
      next: drivers => {
        this.drivers = drivers.filter(driver => !this.isInactiveDriver(driver));
        const selectedDriverId = Number(this.tripForm.get('driverID')?.value);
        if (selectedDriverId && !this.drivers.some(driver => driver.userID === selectedDriverId)) {
          this.tripForm.get('driverID')?.setValue(null, { emitEvent: false });
        }
      },
      error: () => this.driverValidationMessage = 'Unable to load active drivers for this organisation.'
    });
  }

  loadRecurringDrivers(organisationId?: number | null): void {
    if (!organisationId) {
      this.recurringDrivers = [];
      this.recurringForm.get('driverID')?.setValue(null, { emitEvent: false });
      return;
    }

    this.driverService.getDrivers({ organisationId, includeInactive: false }).subscribe({
      next: drivers => {
        this.recurringDrivers = drivers.filter(driver => !this.isInactiveDriver(driver));
        const selectedDriverId = Number(this.recurringForm.get('driverID')?.value);
        if (selectedDriverId && !this.recurringDrivers.some(driver => driver.userID === selectedDriverId)) {
          this.recurringForm.get('driverID')?.setValue(null, { emitEvent: false });
        }
      },
      error: () => this.errorMessage = 'Unable to load active drivers for the recurring schedule.'
    });
  }

  startCreateRecurringSchedule(): void {
    this.showRecurringForm = true;
    this.selectedRecurringSchedule = null;
    this.recurringSubmissionAttempted = false;
    this.selectedWeekdays.clear();
    this.errorMessage = '';
    this.successMessage = '';
    this.recurringForm.reset({
      tripNameTemplate: '', routeID: null, vehicleID: null, driverID: null,
      tripCapacity: null, startTime: '', durationMinutes: 60, frequency: 'Daily', interval: 1,
      startDate: this.toDateInput(new Date()), endDate: '', occurrenceCount: null,
      operatorTimeZone: 'Africa/Johannesburg', statusID: StatusID.ACTIVE, generateDaysAhead: 30
    });
    this.recurringDrivers = [];
  }

  editRecurringSchedule(schedule: RecurringTripSchedule): void {
    this.showRecurringForm = true;
    this.selectedRecurringSchedule = schedule;
    this.recurringSubmissionAttempted = false;
    this.selectedWeekdays = new Set((schedule.weekdays || []).map(Number));
    this.recurringForm.reset({
      tripNameTemplate: schedule.tripNameTemplate,
      routeID: schedule.routeID,
      vehicleID: schedule.vehicleID,
      driverID: schedule.driverID,
      tripCapacity: schedule.tripCapacity,
      startTime: String(schedule.startTime || '').slice(0, 5),
      durationMinutes: this.timeSpanToMinutes(schedule.duration),
      frequency: schedule.frequency,
      interval: schedule.interval,
      startDate: schedule.startDate,
      endDate: schedule.endDate || '',
      occurrenceCount: schedule.occurrenceCount || null,
      operatorTimeZone: schedule.operatorTimeZone || 'Africa/Johannesburg',
      statusID: schedule.statusID,
      generateDaysAhead: 30
    }, { emitEvent: false });
    this.onRecurringRouteOrVehicleChanged();
  }

  saveRecurringSchedule(): void {
    this.recurringSubmissionAttempted = true;
    this.recurringForm.markAllAsTouched();
    this.errorMessage = '';
    const weeklyMissingDays = this.recurringForm.value.frequency === 'Weekly' && this.selectedWeekdays.size === 0;
    if (this.recurringForm.invalid || weeklyMissingDays) return;

    const payload = this.buildRecurringPayload();
    const request = this.selectedRecurringSchedule
      ? this.recurringTripService.updateSchedule(this.selectedRecurringSchedule.recurringTripScheduleID, payload)
      : this.recurringTripService.createSchedule(payload);

    this.isSavingRecurring = true;
    request.pipe(finalize(() => this.isSavingRecurring = false)).subscribe({
      next: () => {
        this.successMessage = this.selectedRecurringSchedule ? 'Recurring schedule updated.' : 'Recurring schedule created and trips generated.';
        this.showRecurringForm = false;
        this.selectedRecurringSchedule = null;
        this.loadRecurringSchedules();
        this.loadTrips();
      },
      error: error => this.errorMessage = this.extractError(error, 'Unable to save the recurring schedule.')
    });
  }

  async deactivateRecurringSchedule(schedule: RecurringTripSchedule): Promise<void> {
    if (!await this.confirmations.confirm({
      title: 'Deactivate recurring schedule',
      message: `Deactivate recurring schedule ${schedule.tripNameTemplate}? Existing trips will remain available.`,
      confirmLabel: 'Deactivate',
      tone: 'danger'
    })) return;
    this.recurringTripService.deactivateSchedule(schedule.recurringTripScheduleID).subscribe({
      next: () => {
        this.successMessage = 'Recurring schedule deactivated.';
        this.loadRecurringSchedules();
      },
      error: error => this.errorMessage = this.extractError(error, 'Unable to deactivate the recurring schedule.')
    });
  }

  generateRecurringTrips(schedule: RecurringTripSchedule): void {
    this.recurringTripService.generateTrips(schedule.recurringTripScheduleID).subscribe({
      next: occurrences => {
        const generated = occurrences.filter(item => item.status === 'generated').length;
        this.successMessage = generated ? `${generated} trip${generated === 1 ? '' : 's'} generated.` : 'Recurring trips are already up to date.';
        this.loadTrips();
      },
      error: error => this.errorMessage = this.extractError(error, 'Unable to generate recurring trips.')
    });
  }

  onRecurringRouteOrVehicleChanged(): void {
    this.loadRecurringDrivers(this.getRecurringOrganisationId());
  }

  toggleWeekday(day: number, checked: boolean): void {
    checked ? this.selectedWeekdays.add(day) : this.selectedWeekdays.delete(day);
  }

  isWeekdaySelected(day: number): boolean {
    return this.selectedWeekdays.has(day);
  }

  getFilteredRecurringRoutes(): Route[] {
    const vehicle = this.vehicles.find(item => item.vehicleID === Number(this.recurringForm.get('vehicleID')?.value));
    return this.routes.filter(route => !this.isInactiveRoute(route) && (!vehicle?.organisationID || route.organisationID === vehicle.organisationID));
  }

  getFilteredRecurringVehicles(): Vehicle[] {
    const route = this.routes.find(item => item.routeID === Number(this.recurringForm.get('routeID')?.value));
    return this.vehicles.filter(vehicle => !route?.organisationID || vehicle.organisationID === route.organisationID);
  }

  getRecurrenceLabel(schedule: RecurringTripSchedule): string {
    if (schedule.frequency === 'Weekly') {
      const days = (schedule.weekdays || []).map(day => this.weekdays.find(item => item.value === Number(day))?.label).filter(Boolean).join(', ');
      return `${schedule.interval > 1 ? `Every ${schedule.interval} weeks` : 'Weekly'}${days ? ` on ${days}` : ''}`;
    }
    if (schedule.frequency === 'EveryNDays' || schedule.interval > 1) return `Every ${schedule.interval} days`;
    return 'Daily';
  }

  isRecurringInactive(schedule: RecurringTripSchedule): boolean {
    return Number(schedule.statusID) === StatusID.INACTIVE || String(schedule.statusName || '').toLowerCase() === 'inactive';
  }

  isRecurringInvalid(controlName: string): boolean {
    const control = this.recurringForm.get(controlName);
    return Boolean(control?.invalid && (control.touched || control.dirty || this.recurringSubmissionAttempted));
  }

  startCreateTrip(): void {
    this.showForm = true;
    this.isEditing = false;
    this.selectedTrip = null;
    this.duplicateSource = null;
    this.formSubmissionAttempted = false;
    this.errorMessage = '';
    this.successMessage = '';
    this.driverValidationMessage = '';
    this.organisationValidationMessage = '';
    this.tripForm.reset({
      tripName: '',
      routeID: null,
      vehicleID: null,
      driverID: null,
      tripStartTime: '',
      tripEndTime: '',
      tripCapacity: null,
      tripSeatsAvailable: null
    });
    this.drivers = [];
  }

  onSubmit(): void {
    this.formSubmissionAttempted = true;
    this.tripForm.markAllAsTouched();
    this.errorMessage = '';
    this.successMessage = '';
    this.driverValidationMessage = '';
    this.validateOrganisationMatch();

    if (this.tripForm.invalid || this.organisationValidationMessage) {
      return;
    }

    const payload = this.buildTripPayload();
    const request = this.duplicateSource
      ? this.tripService.duplicateTrip(this.duplicateSource.tripID, {
          tripName: payload.tripName,
          tripStartTime: payload.tripStartTime || '',
          tripEndTime: payload.tripEndTime || '',
          vehicleID: payload.vehicleID,
          driverID: payload.driverID
        })
      : this.isEditing && this.selectedTrip
      ? this.tripService.updateTrip(this.selectedTrip.tripID, payload)
      : this.tripService.createTrip(payload);

    request.subscribe({
      next: () => {
        this.successMessage = this.duplicateSource ? 'Trip duplicated.' : this.isEditing ? 'Trip updated.' : 'Trip scheduled.';
        this.loadTrips();
        this.resetForm();
      },
      error: error => {
        const message = this.extractError(error, 'Unable to save trip.');
        if (this.isDriverValidationError(message)) {
          this.driverValidationMessage = message;
          return;
        }
        this.errorMessage = message;
      }
    });
  }

  editTrip(trip: Trip): void {
    this.selectedTrip = trip;
    this.isEditing = true;
    this.showForm = true;
    this.duplicateSource = null;
    this.formSubmissionAttempted = false;
    this.errorMessage = '';
    this.successMessage = '';
    this.driverValidationMessage = this.getTripDriverId(trip) ? '' : 'This older trip has no driver assigned. Select an active driver before saving.';
    this.organisationValidationMessage = '';
    
    this.tripForm.reset({
      tripName: trip.tripName,
      routeID: this.getTripRouteId(trip),
      vehicleID: this.getTripVehicleId(trip),
      driverID: this.getTripDriverId(trip),
      tripStartTime: this.toLocalDateTime(trip.tripStartTime || trip.tripStartDateTime),
      tripEndTime: this.toLocalDateTime(trip.tripEndTime || trip.tripEndDateTime),
      tripCapacity: trip.tripCapacity ?? trip.availableSeats,
      tripSeatsAvailable: trip.tripSeatsAvailable ?? trip.availableSeats
    }, { emitEvent: false });
    this.onRouteOrVehicleChanged(false);
  }

  startDuplicateTrip(trip: Trip): void {
    this.startCreateTrip();
    this.duplicateSource = trip;
    const sourceStart = new Date(this.getTripStart(trip));
    const sourceEnd = new Date(this.getTripEnd(trip));
    const duration = Math.max(0, sourceEnd.getTime() - sourceStart.getTime());
    const nextStart = new Date(sourceStart);
    nextStart.setDate(nextStart.getDate() + 1);
    const nextEnd = new Date(nextStart.getTime() + duration);
    this.tripForm.reset({
      tripName: `${trip.tripName} COPY`,
      routeID: this.getTripRouteId(trip),
      vehicleID: this.getTripVehicleId(trip),
      driverID: this.getTripDriverId(trip),
      tripStartTime: this.toLocalDateTime(nextStart.toISOString()),
      tripEndTime: this.toLocalDateTime(nextEnd.toISOString()),
      tripCapacity: trip.tripCapacity ?? trip.availableSeats,
      tripSeatsAvailable: trip.tripCapacity ?? trip.availableSeats
    }, { emitEvent: false });
    this.onRouteOrVehicleChanged(false);
  }

  async deleteTrip(trip: Trip): Promise<void> {
    const tripStart = this.getTripStart(trip);
    const dateLabel = tripStart
      ? new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(tripStart))
      : 'its scheduled date';

    if (!await this.confirmations.confirm({
      title: 'Delete trip',
      message: `Delete ${trip.tripName} scheduled for ${dateLabel}? This cannot be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger'
    })) return;

    this.errorMessage = '';
    this.successMessage = '';
    this.tripService.deleteTrip(trip.tripID).subscribe({
      next: () => {
        if (this.selectedTrip?.tripID === trip.tripID || this.duplicateSource?.tripID === trip.tripID) {
          this.resetForm();
        }
        this.successMessage = 'Trip deleted.';
        this.loadTrips();
      },
      error: error => this.errorMessage = this.extractError(error, 'Unable to delete the trip.')
    });
  }

  resetForm(): void {
    this.tripForm.reset();
    this.showForm = false;
    this.isEditing = false;
    this.selectedTrip = null;
    this.duplicateSource = null;
    this.formSubmissionAttempted = false;
    this.driverValidationMessage = '';
    this.organisationValidationMessage = '';
  }

  onRouteOrVehicleChanged(_clearDriver = true): void {
    this.organisationValidationMessage = '';
    const organisationId = this.getSelectedOrganisationId();
    this.loadDrivers(organisationId);
    this.validateOrganisationMatch();
  }

  getFilteredRoutes(): Route[] {
    const selectedVehicle = this.getSelectedVehicle();
    const activeRoutes = this.routes.filter(route => !this.isInactiveRoute(route));
    if (!selectedVehicle?.organisationID) {
      return activeRoutes;
    }
    return activeRoutes.filter(route => route.organisationID === selectedVehicle.organisationID);
  }

  getFilteredVehicles(): Vehicle[] {
    const selectedRoute = this.getSelectedRoute();
    if (!selectedRoute?.organisationID) {
      return this.vehicles;
    }
    return this.vehicles.filter(vehicle => vehicle.organisationID === selectedRoute.organisationID);
  }

  getDriverName(driver: Driver | null | undefined): string {
    if (!driver) {
      return 'Missing driver';
    }
    return [driver.userFirstName, driver.userLastName].filter(Boolean).join(' ') || driver.username || `Driver ${driver.userID}`;
  }

  getTripDriverName(trip: Trip): string {
    const driverId = this.getTripDriverId(trip);
    const nestedDriver = trip.driver as Driver | null | undefined;
    const driver = nestedDriver?.userID
      ? nestedDriver
      : this.tableDrivers.find(item => item.userID === driverId) || this.drivers.find(item => item.userID === driverId);

    return driverId ? this.getDriverName(driver) : 'Missing driver';
  }

  getTripRouteName(trip: Trip): string {
    const routeId = this.getTripRouteId(trip);
    const route = this.routes.find(item => item.routeID === routeId);
    const directName = this.pickString(trip, ['routeName', 'RouteName']);
    return trip.route?.routeName || directName || route?.routeName || (routeId ? `Route ${routeId}` : '-');
  }

  getTripVehicleLabel(trip: Trip): string {
    const vehicleId = this.getTripVehicleId(trip);
    const vehicle = this.vehicles.find(item => item.vehicleID === vehicleId);
    const identificationNumber = trip.vehicle?.vehicleIdentificationNumber
      || this.pickString(trip, ['vehicleIdentificationNumber', 'VehicleIdentificationNumber'])
      || vehicle?.vehicleIdentificationNumber;
    const registrationNumber = trip.vehicle?.vehicleRegistration
      || this.pickString(trip, ['vehicleRegistration', 'VehicleRegistration'])
      || vehicle?.vehicleRegistration;
    const vehicleIdentity = this.combineVehicleIdentity(identificationNumber, registrationNumber);

    return vehicleIdentity
      || trip.vehicle?.vehicleLabel
      || this.pickString(trip, ['vehicleLabel', 'VehicleLabel'])
      || vehicle?.vehicleLabel
      || (vehicleId ? `Vehicle ${vehicleId}` : '-');
  }

  getTripDriverId(trip: Trip): number | null {
    return this.pickNumber(trip, ['driverID', 'driverId', 'DriverID', 'driverUserID', 'driverUserId', 'DriverUserID'])
      || this.pickNumber(trip.driver, ['userID', 'userId', 'UserID'])
      || null;
  }

  getTripRouteId(trip: Trip): number | null {
    return this.pickNumber(trip, ['routeID', 'routeId', 'RouteID']) || null;
  }

  getTripVehicleId(trip: Trip): number | null {
    return this.pickNumber(trip, ['vehicleID', 'vehicleId', 'VehicleID']) || null;
  }

  getTripStatusLabel(trip: Trip): string {
    const status = this.pickString(trip, ['tripStatus', 'tripStatusName', 'statusName', 'TripStatus', 'TripStatusName', 'StatusName']);
    const statusId = this.pickNumber(trip, ['tripStatusID', 'tripStatusId', 'statusID', 'statusId', 'TripStatusID', 'StatusID']);

    if (status) {
      return status.replace(/_/g, ' ');
    }

    return statusId ? `Status ${statusId}` : 'Unspecified';
  }

  getTripStart(trip: Trip): string {
    return trip.tripStartTime || trip.tripStartDateTime;
  }

  getTripEnd(trip: Trip): string {
    return trip.tripEndTime || trip.tripEndDateTime;
  }

  get upcomingTrips(): Trip[] {
    const now = Date.now();
    return this.trips.filter(trip => {
      const startTime = new Date(this.getTripStart(trip)).getTime();
      return Number.isFinite(startTime) && startTime >= now;
    });
  }

  getCalendarTitle(): string {
    return new Intl.DateTimeFormat('en-ZA', {
      month: 'long',
      year: 'numeric'
    }).format(this.calendarDate);
  }

  previousCalendarMonth(): void {
    this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() - 1, 1);
  }

  nextCalendarMonth(): void {
    this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + 1, 1);
  }

  getCalendarDays(): CalendarDay[] {
    const year = this.calendarDate.getFullYear();
    const month = this.calendarDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();
    const calendarStart = new Date(year, month, 1 - startOffset);

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(calendarStart.getFullYear(), calendarStart.getMonth(), calendarStart.getDate() + index);
      return {
        date,
        dayNumber: date.getDate(),
        isCurrentMonth: date.getMonth() === month,
        isToday: this.isSameDay(date, new Date()),
        trips: this.getTripsForDate(date)
      };
    });
  }

  getCalendarWeekdays(): string[] {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }

  getTripCalendarTime(trip: Trip): string {
    const start = this.getTripStart(trip);
    if (!start) {
      return '--:--';
    }

    return new Intl.DateTimeFormat('en-ZA', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(start));
  }

  isInvalid(controlName: string): boolean {
    const control = this.tripForm.get(controlName);
    return Boolean(control?.invalid && (control.touched || control.dirty || this.formSubmissionAttempted));
  }

  get hasTripFormErrors(): boolean {
    return this.formSubmissionAttempted && (this.tripForm.invalid || Boolean(this.organisationValidationMessage));
  }

  private buildTripPayload(): TripPayload {
    const raw = this.tripForm.value;
    return {
      tripID: this.selectedTrip?.tripID || 0,
      tripName: String(raw.tripName || '').trim(),
      tripCapacity: Number(raw.tripCapacity),
      tripSeatsAvailable: Number(raw.tripSeatsAvailable),
      tripStartTime: new Date(raw.tripStartTime).toISOString(),
      tripEndTime: new Date(raw.tripEndTime).toISOString(),
      vehicleID: Number(raw.vehicleID),
      routeID: Number(raw.routeID),
      driverID: Number(raw.driverID)
    };
  }

  private buildRecurringPayload(): RecurringTripSchedulePayload {
    const raw = this.recurringForm.getRawValue();
    const route = this.routes.find(item => item.routeID === Number(raw.routeID));
    const vehicle = this.vehicles.find(item => item.vehicleID === Number(raw.vehicleID));
    const minutes = Number(raw.durationMinutes);
    return {
      recurringTripScheduleID: this.selectedRecurringSchedule?.recurringTripScheduleID || null,
      organisationID: Number(route?.organisationID || vehicle?.organisationID || this.currentUser?.organisationID),
      routeID: Number(raw.routeID),
      vehicleID: Number(raw.vehicleID),
      driverID: Number(raw.driverID),
      tripNameTemplate: String(raw.tripNameTemplate || '').trim(),
      tripCapacity: Number(raw.tripCapacity),
      startTime: `${String(raw.startTime || '00:00')}:00`,
      duration: this.minutesToTimeSpan(minutes),
      frequency: raw.frequency,
      interval: Number(raw.interval),
      weekdays: raw.frequency === 'Weekly' ? [...this.selectedWeekdays].sort((a, b) => a - b) : [],
      startDate: raw.startDate,
      endDate: raw.endDate || null,
      occurrenceCount: raw.occurrenceCount ? Number(raw.occurrenceCount) : null,
      operatorTimeZone: String(raw.operatorTimeZone || 'Africa/Johannesburg'),
      statusID: Number(raw.statusID),
      generateDaysAhead: Number(raw.generateDaysAhead)
    };
  }

  private getTripsForDate(date: Date): Trip[] {
    return this.trips.filter(trip => {
      const start = this.getTripStart(trip);
      return start ? this.isSameDay(new Date(start), date) : false;
    });
  }

  private isSameDay(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear()
      && first.getMonth() === second.getMonth()
      && first.getDate() === second.getDate();
  }

  private orderTrips(trips: Trip[]): Trip[] {
    return [...trips].sort((first, second) => {
      const firstTime = new Date(this.getTripStart(first) || 0).getTime();
      const secondTime = new Date(this.getTripStart(second) || 0).getTime();
      return firstTime - secondTime;
    });
  }

  private getSelectedOrganisationId(): number | null {
    const route = this.getSelectedRoute();
    const vehicle = this.getSelectedVehicle();

    if (route?.organisationID && vehicle?.organisationID && route.organisationID !== vehicle.organisationID) {
      return null;
    }

    return route?.organisationID || vehicle?.organisationID || this.currentUser?.organisationID || null;
  }

  private getRecurringOrganisationId(): number | null {
    const route = this.routes.find(item => item.routeID === Number(this.recurringForm.get('routeID')?.value));
    const vehicle = this.vehicles.find(item => item.vehicleID === Number(this.recurringForm.get('vehicleID')?.value));
    if (route?.organisationID && vehicle?.organisationID && route.organisationID !== vehicle.organisationID) return null;
    return route?.organisationID || vehicle?.organisationID || this.currentUser?.organisationID || null;
  }

  private validateOrganisationMatch(): void {
    const route = this.getSelectedRoute();
    const vehicle = this.getSelectedVehicle();
    const driver = this.drivers.find(item => item.userID === Number(this.tripForm.get('driverID')?.value));

    if (route?.organisationID && vehicle?.organisationID && route.organisationID !== vehicle.organisationID) {
      this.organisationValidationMessage = 'Selected route and vehicle must belong to the same organisation.';
      return;
    }

    const organisationId = route?.organisationID || vehicle?.organisationID || null;
    if (driver && organisationId && driver.organisationID !== organisationId) {
      this.organisationValidationMessage = 'Selected driver must belong to the same organisation as the route and vehicle.';
      return;
    }

    this.organisationValidationMessage = '';
  }

  private getSelectedRoute(): Route | undefined {
    return this.routes.find(route => route.routeID === Number(this.tripForm.get('routeID')?.value));
  }

  private getSelectedVehicle(): Vehicle | undefined {
    return this.vehicles.find(vehicle => vehicle.vehicleID === Number(this.tripForm.get('vehicleID')?.value));
  }

  private isInactiveRoute(route: Route): boolean {
    return Number(route.statusID) === StatusID.INACTIVE
      || String(route.statusName || '').toLowerCase() === 'inactive';
  }

  private isInactiveDriver(driver: Driver): boolean {
    return Number(driver.statusID) === StatusID.INACTIVE
      || String(driver.statusName || '').toLowerCase() === 'inactive';
  }

  private toLocalDateTime(value?: string): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 16);
  }

  private toDateInput(value: Date): string {
    const localDate = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
  }

  private minutesToTimeSpan(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(remaining).padStart(2, '0')}:00`;
  }

  private timeSpanToMinutes(value: string): number {
    const [hours, minutes] = String(value || '').split(':').map(Number);
    return (Number(hours) || 0) * 60 + (Number(minutes) || 0);
  }

  private isDriverValidationError(message: string): boolean {
    const normalized = message.toLowerCase();
    return normalized.includes('driver') || normalized.includes('inactive') || normalized.includes('organisation') || normalized.includes('role');
  }

  private extractError(error: any, fallback: string): string {
    const validationErrors = error?.error?.errors;
    if (validationErrors && typeof validationErrors === 'object') {
      const messages = Object.values(validationErrors).flatMap(value => Array.isArray(value) ? value : [value]);
      if (messages.length) return messages.map(String).join(' ');
    }
    return error?.error?.message
      || error?.error?.title
      || (typeof error?.error === 'string' ? error.error : '')
      || error?.message
      || fallback;
  }

  private pickNumber(source: any, keys: string[]): number | null {
    if (!source || typeof source !== 'object') {
      return null;
    }

    for (const key of keys) {
      const value = source[key];
      const numericValue = Number(value);
      if (value !== undefined && value !== null && Number.isFinite(numericValue) && numericValue > 0) {
        return numericValue;
      }
    }

    return null;
  }

  private pickString(source: any, keys: string[]): string {
    if (!source || typeof source !== 'object') {
      return '';
    }

    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && String(value).trim()) {
        return String(value).trim();
      }
    }

    return '';
  }

  private combineVehicleIdentity(identificationNumber?: string | null, registrationNumber?: string | null): string {
    return [identificationNumber, registrationNumber]
      .map(value => String(value || '').trim())
      .filter(Boolean)
      .join(' / ');
  }
}
